import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  Send,
  Clock,
  Check,
  AlertCircle,
  Loader2,
  Leaf,
  ChevronRight,
  UtensilsCrossed,
  FileText,
  Menu,
  Search,
  RefreshCw,
  User,
  Gift,
  Heart,
  Mail,
  Phone,
  MessageSquare,
  ChefHat,
  CookingPot,
  CircleDot,
} from 'lucide-react';
import qrPublicService from '../../services/qrPublicService';

// API base URL for resolving image paths
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8081';

function resolveImageUrl(imageUrl) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  // QR menu API returns bare filenames (e.g. "6_123.png") without path prefix.
  // Ensure the /api/uploads/menu-items/ prefix is present.
  if (!imageUrl.startsWith('/')) {
    imageUrl = `/api/uploads/menu-items/${imageUrl}`;
  }
  return `${API_BASE}${imageUrl}`;
}

// ─── Welcome Form ─────────────────────────────────────────────────────────────
function WelcomeForm({ branchName, tableName, onContinue }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [anniversary, setAnniversary] = useState('');

  const handleContinue = (e) => {
    e.preventDefault();
    onContinue({
      customerName: name.trim() || null,
      customerPhone: phone.trim() || null,
      customerEmail: email.trim() || null,
      customerBirthday: birthday || null,
      customerAnniversary: anniversary || null,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed size={28} className="text-teal-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">{branchName || 'Welcome!'}</h1>
          {tableName && (
            <p className="text-sm text-slate-500 mt-1">Table: {tableName}</p>
          )}
          <p className="text-xs text-slate-400 mt-2">Tell us a bit about yourself for a better experience</p>
        </div>

        {/* Form */}
        <form onSubmit={handleContinue} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <User size={12} /> Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <Phone size={12} /> Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <Mail size={12} /> Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          {/* Birthday & Anniversary side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Gift size={12} /> Birthday
              </label>
              <input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Heart size={12} /> Anniversary
              </label>
              <input
                type="date"
                value={anniversary}
                onChange={(e) => setAnniversary(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
              />
            </div>
          </div>

          {/* Button */}
          <div className="pt-2">
            <button
              type="submit"
              className="w-full py-3 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors"
            >
              Continue to Menu
            </button>
          </div>

          <p className="text-[10px] text-slate-400 text-center">All fields are optional</p>
        </form>
      </div>
    </div>
  );
}

// ─── Customer Info Modal (bottom sheet) ───────────────────────────────────────
function CustomerInfoModal({ open, onClose, customerInfo, onSave, saving }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [anniversary, setAnniversary] = useState('');

  useEffect(() => {
    if (open && customerInfo) {
      setName(customerInfo.customerName || '');
      setPhone(customerInfo.customerPhone || '');
      setEmail(customerInfo.customerEmail || '');
      setBirthday(customerInfo.customerBirthday || '');
      setAnniversary(customerInfo.customerAnniversary || '');
    }
  }, [open, customerInfo]);

  if (!open) return null;

  const handleSave = () => {
    onSave({
      customerName: name.trim() || null,
      customerPhone: phone.trim() || null,
      customerEmail: email.trim() || null,
      customerBirthday: birthday || null,
      customerAnniversary: anniversary || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mt-auto bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <User size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-900">Your Information</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <User size={12} /> Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <Phone size={12} /> Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
              <Mail size={12} /> Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Gift size={12} /> Birthday
              </label>
              <input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Heart size={12} /> Anniversary
              </label>
              <input
                type="date"
                value={anniversary}
                onChange={(e) => setAnniversary(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <><Loader2 size={16} className="animate-spin" /> Saving...</>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Category Drawer (slide-in from left) ────────────────────────────────────
function CategoryDrawer({ open, onClose, categories, activeId, onSelect, itemCounts }) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="px-5 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold">Menu Categories</h2>
            <p className="text-[10px] text-teal-200 mt-0.5">{categories.length} categories</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Category list */}
        <div className="flex-1 overflow-y-auto py-2">
          {categories.map((cat) => {
            const isActive = activeId === cat.id;
            const count = itemCounts[cat.id] || 0;
            return (
              <button
                key={cat.id}
                onClick={() => { onSelect(cat.id); onClose(); }}
                className={`w-full text-left px-5 py-3 flex items-center justify-between transition-colors ${
                  isActive
                    ? 'bg-teal-50 border-r-[3px] border-teal-600'
                    : 'hover:bg-slate-50'
                }`}
              >
                <span className={`text-sm ${isActive ? 'font-semibold text-teal-700' : 'font-medium text-slate-700'}`}>
                  {cat.name}
                </span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                  isActive ? 'bg-teal-100 text-teal-700 font-semibold' : 'bg-slate-100 text-slate-400'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Variant Selection Modal (mobile-friendly popup like POS) ────────────────
function VariantModal({ item, onSelectVariant, onClose }) {
  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-sm mx-4 mb-0 sm:mb-0 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {item.isVeg ? (
                  <span className="w-4 h-4 rounded-sm border-2 border-white/70 flex items-center justify-center flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-white/70" />
                  </span>
                ) : (
                  <span className="w-4 h-4 rounded-sm border-2 border-red-300 flex items-center justify-center flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-red-300" />
                  </span>
                )}
                <div>
                  <h3 className="text-base font-bold">{item.name}</h3>
                  <p className="text-xs text-teal-200">Select a variant</p>
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Variants List */}
          <div className="p-4 space-y-2.5 max-h-[50vh] overflow-y-auto">
            {item.variants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => {
                  onSelectVariant(item, variant);
                  onClose();
                }}
                disabled={variant.isAvailable === false}
                className="w-full flex items-center justify-between bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-300 rounded-xl px-4 py-3.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="text-sm font-semibold text-slate-900">{variant.variantName}</span>
                <span className="text-sm font-bold text-teal-700">{Number(variant.variantPrice).toFixed(2)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Menu Item Card ──────────────────────────────────────────────────────────
function MenuItemCard({ item, getCartQuantity, onAdd, onRemove, onShowVariants }) {
  const imgSrc = resolveImageUrl(item.imageUrl);
  const hasVariants = item.variants && item.variants.length > 0;

  // For items with variants, show price range
  const displayPrice = hasVariants
    ? item.variants.length === 1
      ? Number(item.variants[0].variantPrice).toFixed(2)
      : `${Number(Math.min(...item.variants.map(v => Number(v.variantPrice)))).toFixed(2)}+`
    : Number(item.basePrice).toFixed(2);

  // Total quantity across all variants (or base item)
  const totalQuantity = hasVariants
    ? item.variants.reduce((sum, v) => sum + getCartQuantity(item.id, v.id), 0)
    : getCartQuantity(item.id, null);

  const handleAdd = () => {
    if (hasVariants) {
      onShowVariants(item);
    } else {
      onAdd(item, null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex">
      {/* Image */}
      {imgSrc && (
        <div className="w-24 h-24 flex-shrink-0 bg-slate-100">
          <img
            src={imgSrc}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      {/* Info */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            {item.isVeg ? (
              <span className="w-3.5 h-3.5 rounded-sm border-2 border-emerald-600 flex items-center justify-center flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              </span>
            ) : (
              <span className="w-3.5 h-3.5 rounded-sm border-2 border-red-600 flex items-center justify-center flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
              </span>
            )}
            <h3 className="text-sm font-semibold text-slate-900 truncate">{item.name}</h3>
          </div>
          {item.description && (
            <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">{item.description}</p>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-bold text-slate-900">{displayPrice}</span>
          {totalQuantity > 0 ? (
            <div className="flex items-center gap-0 bg-teal-600 rounded-lg overflow-hidden">
              {hasVariants ? (
                // For variant items: minus removes last added variant, plus opens modal
                <>
                  <button onClick={() => {
                    // Find the last variant in cart for this item and remove it
                    const lastVariant = item.variants.find(v => getCartQuantity(item.id, v.id) > 0);
                    if (lastVariant) onRemove(item.id, lastVariant.id);
                  }} className="px-2 py-1 text-white hover:bg-teal-700 transition-colors">
                    <Minus size={14} />
                  </button>
                  <span className="px-2 py-1 text-xs font-bold text-white min-w-[24px] text-center">{totalQuantity}</span>
                  <button onClick={handleAdd} className="px-2 py-1 text-white hover:bg-teal-700 transition-colors">
                    <Plus size={14} />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => onRemove(item.id, null)} className="px-2 py-1 text-white hover:bg-teal-700 transition-colors">
                    <Minus size={14} />
                  </button>
                  <span className="px-2 py-1 text-xs font-bold text-white min-w-[24px] text-center">{totalQuantity}</span>
                  <button onClick={() => onAdd(item, null)} className="px-2 py-1 text-white hover:bg-teal-700 transition-colors">
                    <Plus size={14} />
                  </button>
                </>
              )}
            </div>
          ) : (
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
            >
              ADD
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Cart Drawer ─────────────────────────────────────────────────────────────
function CartDrawer({ open, onClose, cart, onAdd, onRemove, onClear, onPlaceOrder, placing, notes, setNotes, onUpdateItemInstructions }) {
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mt-auto bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShoppingCart size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-900">Your Order</h2>
            <span className="text-xs text-slate-400">({totalItems} item{totalItems !== 1 ? 's' : ''})</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart size={32} className="text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400">Your cart is empty</p>
            </div>
          ) : (
            <>
              {cart.map((item) => (
                <div key={item.cartKey} className="py-2 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                      {item.variantName && (
                        <p className="text-[11px] text-teal-600 font-medium">{item.variantName}</p>
                      )}
                      <p className="text-xs text-slate-400">{Number(item.price).toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-0 bg-slate-100 rounded-lg">
                      <button onClick={() => onRemove(item.menuItemId, item.variantId)} className="px-2 py-1 text-slate-600 hover:text-red-600 transition-colors">
                        {item.quantity === 1 ? <Trash2 size={13} /> : <Minus size={13} />}
                      </button>
                      <span className="px-2 text-xs font-bold text-slate-900 min-w-[20px] text-center">{item.quantity}</span>
                      <button onClick={() => onAdd({ id: item.menuItemId, name: item.name, basePrice: item.price }, item.variantId ? { id: item.variantId, variantName: item.variantName, variantPrice: item.price } : null)} className="px-2 py-1 text-slate-600 hover:text-teal-600 transition-colors">
                        <Plus size={13} />
                      </button>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 w-16 text-right">
                      {(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                  {/* Per-item special instructions */}
                  <div className="mt-1.5">
                    <button
                      onClick={() => {
                        const el = document.getElementById(`instr-${item.cartKey}`);
                        if (el) el.classList.toggle('hidden');
                      }}
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-teal-600 transition-colors"
                    >
                      <MessageSquare size={10} />
                      {item.specialInstructions ? 'Edit note' : 'Add note'}
                    </button>
                    <input
                      id={`instr-${item.cartKey}`}
                      type="text"
                      value={item.specialInstructions || ''}
                      onChange={(e) => onUpdateItemInstructions(item.cartKey, e.target.value)}
                      placeholder="e.g. No onions, extra spicy..."
                      className={`${item.specialInstructions ? '' : 'hidden'} mt-1 w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-50`}
                    />
                  </div>
                </div>
              ))}

              {/* Order-level notes */}
              <div className="pt-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                  <FileText size={12} /> Order notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any general notes for this order..."
                  rows={2}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 resize-none"
                />
              </div>

              <button onClick={onClear} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                Clear all
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="px-5 py-4 border-t border-slate-100 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-600">Total</span>
              <span className="text-lg font-bold text-slate-900">{total.toFixed(2)}</span>
            </div>
            <button
              onClick={onPlaceOrder}
              disabled={placing}
              className="w-full py-3 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {placing ? (
                <><Loader2 size={16} className="animate-spin" /> Placing Order...</>
              ) : (
                <><Send size={16} /> Place Order</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Order Tracker ───────────────────────────────────────────────────────────
function OrderTracker({ open, orders, onClose, onRefresh, refreshing }) {
  if (!open || !orders || orders.length === 0) return null;

  const getStatusDisplay = (order) => {
    // If order is approved and has a linked real order, show the real order's status
    const realStatus = order.orderStatus;
    if (order.status === 'APPROVED' && realStatus) {
      const s = realStatus.toUpperCase();
      if (s === 'PREPARING' || s === 'SENDING') return { icon: <CookingPot size={14} className="text-orange-500" />, label: 'Being prepared', color: 'text-orange-600', bg: 'bg-orange-50' };
      if (s === 'READY') return { icon: <ChefHat size={14} className="text-emerald-500" />, label: 'Ready to serve', color: 'text-emerald-600', bg: 'bg-emerald-50' };
      if (s === 'SERVED') return { icon: <Check size={14} className="text-blue-500" />, label: 'Served', color: 'text-blue-600', bg: 'bg-blue-50' };
      if (s === 'BILLED') return { icon: <Check size={14} className="text-slate-400" />, label: 'Completed', color: 'text-slate-500', bg: 'bg-slate-50' };
      if (s === 'SENT') return { icon: <Clock size={14} className="text-teal-500" />, label: 'Sent to kitchen', color: 'text-teal-600', bg: 'bg-teal-50' };
    }
    // Fallback to QR order status
    if (order.status === 'PENDING') return { icon: <Clock size={14} className="text-amber-500" />, label: 'Waiting for confirmation', color: 'text-amber-600', bg: 'bg-amber-50' };
    if (order.status === 'APPROVED') return { icon: <Check size={14} className="text-emerald-500" />, label: 'Confirmed & sent to kitchen', color: 'text-emerald-600', bg: 'bg-emerald-50' };
    if (order.status === 'REJECTED') return { icon: <AlertCircle size={14} className="text-red-500" />, label: 'Order rejected', color: 'text-red-600', bg: 'bg-red-50' };
    return { icon: <CircleDot size={14} className="text-slate-400" />, label: order.status, color: 'text-slate-600', bg: 'bg-slate-50' };
  };

  // Progress steps for visual timeline
  const getProgressSteps = (order) => {
    const realStatus = (order.status === 'APPROVED' && order.orderStatus) ? order.orderStatus.toUpperCase() : null;
    if (!realStatus || order.status !== 'APPROVED') return null;

    const steps = [
      { key: 'SENT', label: 'Confirmed' },
      { key: 'PREPARING', label: 'Preparing' },
      { key: 'READY', label: 'Ready' },
      { key: 'SERVED', label: 'Served' },
    ];
    const statusOrder = ['SENT', 'SENDING', 'PREPARING', 'READY', 'SERVED', 'BILLED'];
    const currentIdx = statusOrder.indexOf(realStatus);
    if (currentIdx < 0) return null;

    return steps.map((step) => {
      const stepIdx = statusOrder.indexOf(step.key);
      return { ...step, done: currentIdx >= stepIdx, active: realStatus === step.key || (step.key === 'PREPARING' && realStatus === 'SENDING') };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mt-auto bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Your Orders</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              title="Refresh orders"
            >
              <RefreshCw size={16} className={`text-teal-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              <X size={18} className="text-slate-400" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {orders.map((order) => {
            const statusDisplay = getStatusDisplay(order);
            const progressSteps = getProgressSteps(order);

            return (
              <div key={order.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-700">Order #{order.id}</span>
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${statusDisplay.bg}`}>
                    {statusDisplay.icon}
                    <span className={`text-[11px] font-medium ${statusDisplay.color}`}>
                      {statusDisplay.label}
                    </span>
                  </div>
                </div>

                {/* Progress timeline */}
                {progressSteps && (
                  <div className="flex items-center gap-0 mb-3 px-1">
                    {progressSteps.map((step, idx) => (
                      <React.Fragment key={step.key}>
                        <div className="flex flex-col items-center flex-1">
                          <div className={`w-3 h-3 rounded-full border-2 ${step.done ? 'bg-teal-500 border-teal-500' : 'bg-white border-slate-300'} ${step.active ? 'ring-2 ring-teal-200' : ''}`} />
                          <span className={`text-[9px] mt-0.5 ${step.done ? 'text-teal-700 font-medium' : 'text-slate-400'}`}>{step.label}</span>
                        </div>
                        {idx < progressSteps.length - 1 && (
                          <div className={`flex-1 h-0.5 -mt-3 ${step.done ? 'bg-teal-400' : 'bg-slate-200'}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {order.rejectionReason && (
                  <p className="text-xs text-red-600 mb-2 bg-red-50 px-2 py-1 rounded">
                    Reason: {order.rejectionReason}
                  </p>
                )}
                <div className="space-y-1">
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex-1 min-w-0">
                        <span className="text-slate-600">
                          {item.quantity}x {item.itemName}
                          {item.variantName && <span className="text-teal-600 ml-1">({item.variantName})</span>}
                        </span>
                        {item.specialInstructions && (
                          <p className="text-[10px] text-slate-400 italic truncate">{item.specialInstructions}</p>
                        )}
                      </div>
                      <span className="text-slate-500 ml-2">{Number(item.totalPrice).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                  <span className="text-xs text-slate-400">{order.totalItems} items</span>
                  <span className="text-sm font-bold text-slate-900">{Number(order.totalAmount).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function CustomerMenuPage() {
  const { branchId, token } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [menu, setMenu] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasPlacedOrder, setHasPlacedOrder] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [refreshingOrders, setRefreshingOrders] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [customerInfoOpen, setCustomerInfoOpen] = useState(false);
  const [savingCustomerInfo, setSavingCustomerInfo] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({});
  const [variantItem, setVariantItem] = useState(null);

  // Initialize session
  useEffect(() => {
    if (!token) return;

    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await qrPublicService.createSession(branchId, token);
        setSession(data);
        setMenu(data.menu);
        if (data.menu?.categories?.length > 0) {
          setActiveCategory(data.menu.categories[0].id);
        }

        // Store customer info from response
        const info = {
          customerName: data.customerName || null,
          customerPhone: data.customerPhone || null,
          customerEmail: data.customerEmail || null,
          customerBirthday: data.customerBirthday || null,
          customerAnniversary: data.customerAnniversary || null,
        };
        setCustomerInfo(info);

        // Decide whether to show welcome form
        const hasCustomerInfo = data.customerName || data.customerPhone || data.customerEmail || data.customerBirthday || data.customerAnniversary;
        const skipKey = `qr_info_skipped_${data.sessionToken}`;
        const wasSkipped = sessionStorage.getItem(skipKey);

        if (!hasCustomerInfo && !wasSkipped) {
          setShowWelcome(true);
        }

        // Check for existing orders (session may have been resumed after page refresh)
        if (data.sessionToken) {
          try {
            const existingOrders = await qrPublicService.getOrders(branchId, data.sessionToken);
            if (existingOrders && existingOrders.length > 0) {
              setOrders(existingOrders);
              setHasPlacedOrder(true);
            }
          } catch {}
        }
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || 'Failed to load menu';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [branchId, token]);

  // Poll orders — only after first order is placed (polling only, no immediate fetch)
  useEffect(() => {
    if (!session?.sessionToken || !hasPlacedOrder) return;

    const poll = async () => {
      try {
        const data = await qrPublicService.getOrders(branchId, session.sessionToken);
        setOrders(data || []);
      } catch {}
    };

    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [branchId, session?.sessionToken, hasPlacedOrder]);

  // Welcome form handler — single flow: save info if filled, always proceed
  const handleWelcomeContinue = async (info) => {
    // Always set skip flag so form doesn't reappear on refresh
    if (session?.sessionToken) {
      sessionStorage.setItem(`qr_info_skipped_${session.sessionToken}`, 'true');
    }
    // Save customer info if any field was filled
    const hasAnyInfo = info.customerName || info.customerPhone || info.customerEmail || info.customerBirthday || info.customerAnniversary;
    if (session?.sessionToken && hasAnyInfo) {
      try {
        const result = await qrPublicService.updateCustomerInfo(branchId, {
          sessionToken: session.sessionToken,
          ...info,
        });
        setCustomerInfo({
          customerName: result.customerName || info.customerName,
          customerPhone: result.customerPhone || info.customerPhone,
          customerEmail: result.customerEmail || info.customerEmail,
          customerBirthday: result.customerBirthday || info.customerBirthday,
          customerAnniversary: result.customerAnniversary || info.customerAnniversary,
        });
      } catch {}
    }
    setShowWelcome(false);
  };

  // Update customer info from modal
  const handleSaveCustomerInfo = async (info) => {
    if (!session?.sessionToken) return;
    try {
      setSavingCustomerInfo(true);
      const result = await qrPublicService.updateCustomerInfo(branchId, {
        sessionToken: session.sessionToken,
        ...info,
      });
      setCustomerInfo({
        customerName: result.customerName || null,
        customerPhone: result.customerPhone || null,
        customerEmail: result.customerEmail || null,
        customerBirthday: result.customerBirthday || null,
        customerAnniversary: result.customerAnniversary || null,
      });
      setCustomerInfoOpen(false);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to update information';
      alert(message);
    } finally {
      setSavingCustomerInfo(false);
    }
  };

  // Cart operations — cart key = `menuItemId-variantId` (variantId is 'base' for no variant)
  const makeCartKey = (menuItemId, variantId) => `${menuItemId}-${variantId || 'base'}`;

  const addToCart = useCallback((item, variant) => {
    const cartKey = makeCartKey(item.id, variant?.id);
    setCart((prev) => {
      const existing = prev.find((c) => c.cartKey === cartKey);
      if (existing) {
        return prev.map((c) => (c.cartKey === cartKey ? { ...c, quantity: c.quantity + 1 } : c));
      }
      return [...prev, {
        cartKey,
        menuItemId: item.id,
        variantId: variant?.id || null,
        name: item.name,
        variantName: variant?.variantName || null,
        price: Number(variant?.variantPrice ?? item.basePrice),
        quantity: 1,
        specialInstructions: '',
      }];
    });
  }, []);

  const removeFromCart = useCallback((menuItemId, variantId) => {
    const cartKey = makeCartKey(menuItemId, variantId);
    setCart((prev) => {
      const existing = prev.find((c) => c.cartKey === cartKey);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter((c) => c.cartKey !== cartKey);
      return prev.map((c) => (c.cartKey === cartKey ? { ...c, quantity: c.quantity - 1 } : c));
    });
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const getCartQuantity = useCallback((menuItemId, variantId) => {
    const cartKey = makeCartKey(menuItemId, variantId);
    return cart.find((c) => c.cartKey === cartKey)?.quantity || 0;
  }, [cart]);

  const updateItemInstructions = useCallback((cartKey, instructions) => {
    setCart((prev) => prev.map((c) => c.cartKey === cartKey ? { ...c, specialInstructions: instructions } : c));
  }, []);

  const totalItems = cart.reduce((sum, c) => sum + c.quantity, 0);

  // Place order
  const placeOrder = async () => {
    if (cart.length === 0) return;
    try {
      setPlacing(true);
      const items = cart.map((c) => ({
        menuItemId: c.menuItemId,
        variantId: c.variantId || null,
        quantity: c.quantity,
        specialInstructions: c.specialInstructions?.trim() || null,
      }));
      await qrPublicService.placeOrder(branchId, session.sessionToken, items, notes);
      setCart([]);
      setNotes('');
      setCartOpen(false);
      setOrderSuccess(true);
      setHasPlacedOrder(true);
      setTimeout(() => setOrderSuccess(false), 3000);
      // Refresh orders
      const data = await qrPublicService.getOrders(branchId, session.sessionToken);
      setOrders(data || []);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to place order';
      alert(message);
    } finally {
      setPlacing(false);
    }
  };

  // Manual refresh orders
  const refreshOrders = async () => {
    if (!session?.sessionToken) return;
    try {
      setRefreshingOrders(true);
      const data = await qrPublicService.getOrders(branchId, session.sessionToken);
      setOrders(data || []);
    } catch {} finally {
      setRefreshingOrders(false);
    }
  };

  // Item counts per category (for drawer badges)
  const itemCounts = {};
  menu?.categories?.forEach((cat) => {
    itemCounts[cat.id] = cat.items?.length || 0;
  });

  // Active category items with search filter
  const activeItems = (menu?.categories?.find((c) => c.id === activeCategory)?.items || []).filter(
    (item) => !searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCategoryName = menu?.categories?.find((c) => c.id === activeCategory)?.name || '';

  // --- Loading ---
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-teal-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading menu...</p>
        </div>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Unable to Load Menu</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --- Welcome Form ---
  if (showWelcome) {
    return (
      <WelcomeForm
        branchName={menu?.branchName || session?.branchName}
        tableName={session?.tableName}
        onContinue={handleWelcomeContinue}
      />
    );
  }

  // --- Menu ---
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Menu drawer toggle */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="p-2 -ml-1 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <Menu size={18} className="text-slate-600" />
            </button>

            {menu?.branchLogoUrl && !logoError ? (
              <img
                src={resolveImageUrl(menu.branchLogoUrl)}
                alt=""
                className="w-10 h-10 rounded-xl object-cover"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                <UtensilsCrossed size={20} className="text-teal-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-slate-900 truncate">{menu?.branchName || session?.branchName}</h1>
              <p className="text-[11px] text-slate-400">{session?.tableName && `Table: ${session.tableName}`}</p>
            </div>

            {/* Customer info button */}
            <button
              onClick={() => setCustomerInfoOpen(true)}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors relative"
              title="Your information"
            >
              <User size={18} className="text-slate-600" />
              {customerInfo.customerName && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-teal-500 rounded-full border-2 border-white" />
              )}
            </button>
          </div>

          {/* Search + active category label */}
          <div className="flex items-center gap-2 mt-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search in ${activeCategoryName}...`}
                className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 focus:bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded"
                >
                  <X size={12} className="text-slate-400" />
                </button>
              )}
            </div>
            <button
              onClick={() => setDrawerOpen(true)}
              className="flex-shrink-0 px-3 py-2 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-100 transition-colors truncate max-w-[120px]"
            >
              {activeCategoryName}
            </button>
          </div>
        </div>
      </div>

      {/* Order Success Toast */}
      {orderSuccess && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-slide-down">
          <Check size={18} />
          <span className="text-sm font-medium">Order placed successfully!</span>
        </div>
      )}

      {/* Menu Items */}
      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {activeItems.length === 0 ? (
          <div className="text-center py-12">
            <Leaf size={32} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              {searchQuery ? `No items matching "${searchQuery}"` : 'No items in this category'}
            </p>
          </div>
        ) : (
          activeItems.map((item) => (
            <MenuItemCard
              key={item.id}
              item={item}
              getCartQuantity={getCartQuantity}
              onAdd={addToCart}
              onRemove={removeFromCart}
              onShowVariants={setVariantItem}
            />
          ))
        )}
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          {/* Track Orders */}
          {orders.length > 0 && (
            <button
              onClick={() => setTrackingOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              <Clock size={14} />
              Track ({orders.length})
            </button>
          )}

          {/* View Cart */}
          <button
            onClick={() => setCartOpen(true)}
            disabled={totalItems === 0}
            className={`flex-1 flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              totalItems > 0
                ? 'bg-teal-600 text-white hover:bg-teal-700'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-2">
              <ShoppingCart size={16} />
              <span>{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>{cart.reduce((s, c) => s + c.price * c.quantity, 0).toFixed(2)}</span>
              <ChevronRight size={16} />
            </div>
          </button>
        </div>
      </div>

      {/* Category Drawer */}
      <CategoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        categories={menu?.categories || []}
        activeId={activeCategory}
        onSelect={setActiveCategory}
        itemCounts={itemCounts}
      />

      {/* Cart Drawer */}
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onAdd={addToCart}
        onRemove={removeFromCart}
        onClear={clearCart}
        onPlaceOrder={placeOrder}
        placing={placing}
        notes={notes}
        setNotes={setNotes}
        onUpdateItemInstructions={updateItemInstructions}
      />

      {/* Order Tracker */}
      <OrderTracker
        open={trackingOpen}
        orders={orders}
        onClose={() => setTrackingOpen(false)}
        onRefresh={refreshOrders}
        refreshing={refreshingOrders}
      />

      {/* Variant Selection Modal */}
      {variantItem && (
        <VariantModal
          item={variantItem}
          onSelectVariant={addToCart}
          onClose={() => setVariantItem(null)}
        />
      )}

      {/* Customer Info Modal */}
      <CustomerInfoModal
        open={customerInfoOpen}
        onClose={() => setCustomerInfoOpen(false)}
        customerInfo={customerInfo}
        onSave={handleSaveCustomerInfo}
        saving={savingCustomerInfo}
      />

      {/* CSS for animations */}
      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes slide-down {
          from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
